import {FormRenderProps} from 'react-final-form';
import React, {
   CSSProperties,
   forwardRef,
   ReactElement,
   Ref,
   useCallback,
   useEffect,
   useImperativeHandle,
   useMemo,
   useRef,
   useState
} from 'react';
import {DirtyStateChangeEvent, FormContext} from './Form';
import {FormApi, FormApiWrapper} from './FinalFormTypes';
import {BorderPanel} from '../../layout/borderpanel';
import {VerticalPanel} from '../../layout/verticalpanel';
import {ActivityIndicator} from '../../feedback/activityindicator';
import {AlertBanner} from './alert-banner/AlertBanner';
import {FormApi as FinalFormApi} from 'final-form';

export interface NativeFormProps<T> extends FormRenderProps<T> {
   className?: string;
   name: string;
   disableSaveMask?: boolean;
   disableAlertBanner?: boolean;
   disableBeforeUnloadHandler?: boolean;
   style?: CSSProperties;
   verticalFill?: boolean;
   onSavingStateChange?: (saving: boolean) => void;
   onDirtyStateChange?: (event: DirtyStateChangeEvent<T>) => void;
}

const alertBanner = <AlertBanner/>;

const beforeUnload = (event: BeforeUnloadEvent) => {
   event.preventDefault();
   event.returnValue = true;
};

export const NativeForm = forwardRef(function NativeForm<T>(props: NativeFormProps<T>,
                                                            ref: Ref<FormApi<T>>) {
   const {
      form,
      onSavingStateChange,
      onDirtyStateChange,
      disableSaveMask,
      disableAlertBanner,
      disableBeforeUnloadHandler,
      handleSubmit,
      name,
      submitting,
      pristine,
      children: childrenProps
   } = props;

   const formContextData = useMemo(() => {
      return {
         name: name
      };
   }, [name]);

   const [maskForm, setMaskForm] = useState<boolean>(false);
   const saving = useRef<boolean>(submitting);
   const wrappedFormApi = useMemo(() => new FormApiWrapper(form), [form]);

   useImperativeHandle(ref, () => wrappedFormApi, [wrappedFormApi]);

   useEffect(() => {
      if (submitting) {
         if (!saving.current) {
            // Started saving...
            saving.current = true;
            if (onSavingStateChange) {
               onSavingStateChange(true);
            }
            if (disableSaveMask !== true) {
               setMaskForm(true);
            }
         }
      }
      else {
         if (saving.current) {
            // Done saving...
            saving.current = false;
            if (onSavingStateChange) {
               onSavingStateChange(false);
            }
            if (disableSaveMask !== true) {
               setMaskForm(false);
            }
         }
      }
   }, [submitting, disableSaveMask, onSavingStateChange]);

   useEffect(() => {
      if (pristine) {
         if (disableBeforeUnloadHandler === undefined || !disableBeforeUnloadHandler) {
            window.removeEventListener('beforeunload', beforeUnload);
         }

         if (onDirtyStateChange) {
            onDirtyStateChange({
                                  hasUnsavedChanges: false,
                                  dirtyFormValues: {},
                                  formValues: form.getState().values,
                                  form: wrappedFormApi
                               });
         }
      }
      else {
         if (disableBeforeUnloadHandler === undefined || !disableBeforeUnloadHandler) {
            window.addEventListener('beforeunload', beforeUnload);
         }

         if (onDirtyStateChange) {
            onDirtyStateChange({
                                  hasUnsavedChanges: true,
                                  dirtyFormValues: mapDirtyFields(form),
                                  formValues: form.getState().values,
                                  form: wrappedFormApi
                               });
         }
      }
   }, [form, disableBeforeUnloadHandler, onDirtyStateChange, wrappedFormApi, pristine]);

   const style = useMemo(() => {
      return {
         height: (props.verticalFill !== false) ? '100%' : undefined,
         width: '100%',
         ...props.style
      };
   }, [props.style, props.verticalFill]);

   const children = useMemo(() => {
      return (<BorderPanel
         top={disableAlertBanner ? undefined : alertBanner}
      >
         <VerticalPanel scroll={true}>
            <ActivityIndicator spinning={maskForm} wrapperClassName={'uf-form-activity-indicator'}>
               {childrenProps}
            </ActivityIndicator>
         </VerticalPanel>
      </BorderPanel>);
   }, [disableAlertBanner, maskForm, childrenProps]);

   const onSubmitHandler = useCallback((event?: Partial<Pick<React.SyntheticEvent, 'preventDefault' | 'stopPropagation'>>) => {
      const activeField = form.getState().active;
      if (activeField) {
         form.blur(activeField);
      }
      return handleSubmit(event);
   }, [handleSubmit, form]);

   return (
      <FormContext.Provider value={formContextData}>
         <form
            id={name}
            name={name}
            onSubmit={onSubmitHandler}
            style={style}
            className={`uf-form ${props.className || ''}`}
            autoComplete={'off'}
         >
            {children}
         </form>
      </FormContext.Provider>
   );
}) as <T, >(props: NativeFormProps<T> & React.RefAttributes<FormApi<T>>) => ReactElement;

const mapDirtyFields = <T, >(form: FinalFormApi<T>) => {
   const formState = form.getState();
   const dirtyFields = formState.dirtyFields;
   const values = formState.values;

   const result: Partial<T> = {};
   for (const dirtyField in dirtyFields) {
      const key: keyof T = dirtyField as keyof T;
      if (dirtyFields[dirtyField] === true) {
         result[key] = values[key];
      }
   }
   return result;
};
